import React from 'react';
import {
  Button as PrimeButton,
  ButtonProps as PrimeButtonProps,
} from 'primereact/button';

/**
 * Button Type Definitions
 */
export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger';

export type CustomButtonSize =
  | 'sm'
  | 'md'
  | 'lg';

interface GFNButtonProps
  extends Omit<PrimeButtonProps, 'severity' | 'size'> {

  /**
   * Button semantic type
   */
  buttonType?: ButtonType;

  /**
   * Custom size
   */
  size?: CustomButtonSize;

  /**
   * Full width button
   */
  fullWidth?: boolean;

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Test id
   */
  testId?: string;
}

const GFNButton = React.forwardRef<any, GFNButtonProps>(
  (
    {
      buttonType = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      testId,
      className = '',
      disabled = false,
      ...restProps
    },
    ref
  ) => {

    /**
     * PrimeReact severity map
     * Primary handled using custom CSS class
     */
    const severityMap: Record<
      Exclude<ButtonType, 'primary'>,
      'secondary' | 'success' | 'danger'
    > = {
      secondary: 'secondary',
      success: 'success',
      danger: 'danger',
    };

    /**
     * Button size classes
     */
    const sizeClasses = {
      sm: 'p-button-sm',
      md: '',
      lg: 'p-button-lg',
    };

    /**
     * Combined class names
     */
    const combinedClassName = [
      buttonType === 'primary'
        ? 'gfn-primary-button'
        : '',
      sizeClasses[size],
      fullWidth ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <PrimeButton
        ref={ref}
        severity={
          buttonType !== 'primary'
            ? severityMap[
                buttonType as Exclude<ButtonType, 'primary'>
              ]
            : undefined
        }
        className={combinedClassName}
        disabled={disabled || loading}
        loading={loading}
        data-testid={testId}
        {...restProps}
      />
    );
  }
);

GFNButton.displayName = 'GFNButton';

export default GFNButton;
export { GFNButton };